import React, { ReactNode } from 'react';

import DetailKeystore from 'components/wallet/unlock/DetailKeystore';
import DetailPrivateKey from 'components/wallet/unlock/DetailPrivateKey';
// import { unlockSoftwareWalletOrOpenWindowUrl } from 'components/wallet/unlock/unlockSoftwareWallet';
// import UnlockHardwareWallet from 'components/wallet/unlock/hardware/UnlockHardwareWallet';
import { unlockDiscoveredWalletByRdns } from 'lib/ethereum/walletDiscovery/walletDiscovery';
import { unlockCoinbaseWallet } from 'lib/ethereum/wallets/coinbaseWallet';
import {
  getWalletConnectWallet,
  unlockWalletConnect,
} from 'lib/ethereum/wallets/walletConnectV2';
import { openExternalUrl } from 'lib/redirect';
import { Chains } from 'reduxState/exchange/exchangeActiveChainUtils';
import { IWallet } from 'reduxState/wallet/walletReducer';
import { WalletType } from 'reduxState/wallet/walletUtils';

import { MenuTypeCore, walletsMap } from './walletsListCore';

type GetWalletAsyncParams = {
  activeChain: Chains;
  rdns?: string;
};

type RenderParams = {
  onProceed: (wallet: IWallet) => void;
  onBackFromDetail: () => void;
  layout: 'page' | 'modal';
};

interface MenuType extends MenuTypeCore {
  title: string;

  /** For quick unlock items without separate unlock screen */
  getWalletAsync?: ({ activeChain }: GetWalletAsyncParams) => Promise<IWallet>;

  /** Render only for items which are not directly clickable, eg. private keys */
  render?: (renderParams: RenderParams) => ReactNode;
}

/** Legacy web3 Wallet (Before EIP-6963) */
// const softwareWeb3Wallet = (
//   walletType: (typeof softwareWeb3Wallets)[number]
// ): MenuType => ({
//   ...walletsMap[walletType],
//   getWalletAsync: async _renderParams => {
//     const publicKey = await unlockSoftwareWalletOrOpenWindowUrl(walletType);
//     if (!publicKey) {
//       throw new Error('Software wallet not detected.');
//     }
//     return {
//       walletType,
//       walletAddress: publicKey.toLowerCase(),
//       walletAddressOriginal: publicKey,
//     };
//     // await handleUnlock(wallet, renderParams);
//   },
// });

const discoveredWallet = (walletType: WalletType): MenuType => {
  return {
    ...walletsMap[walletType],
    getWalletAsync: async renderParams => {
      const walletConfig = walletsMap[walletType];
      if (walletConfig.rdns && !renderParams.rdns) {
        // Pre-defined wallets
        const walletWebsite = walletConfig.website;
        if (walletWebsite) {
          openExternalUrl(walletWebsite, '_blank');
        }
        throw new Error(
          `We are not able to detect ${walletConfig.title}. Please install it first.`
        );
      }
      if (!renderParams.rdns) {
        throw new Error('RDNS missing');
      }
      const walletAddress = await unlockDiscoveredWalletByRdns(
        renderParams.rdns,
        true
      );
      return {
        walletType: 'softwareDiscovered',
        walletAddress: walletAddress.toLowerCase(),
        walletAddressOriginal: walletAddress,
        walletDiscoveryRdns: renderParams.rdns,
      };
    },
  };
};

export const renderWalletsMap: {
  [key in WalletType]?: MenuType;
} = {
  privateKey: {
    ...walletsMap['privateKey'],
    render: renderParams => (
      <DetailPrivateKey
        onUnlock={async (
          publicKey,
          privateKeyEncrypted,
          personalWalletSecret
        ) => {
          renderParams.onProceed({
            walletType: 'privateKey',
            walletAddress: publicKey.toLowerCase(),
            walletAddressOriginal: publicKey,
            privateKeyEncrypted,
            privateKeyPersonalEncryption: 'unlock',
            personalWalletSecret,
          });
        }}
        onBack={renderParams.onBackFromDetail}
        layout={renderParams.layout}
      />
    ),
  },
  keystore: {
    ...walletsMap['keystore'],
    render: renderParams => (
      <DetailKeystore
        onUnlock={async (
          publicKey,
          privateKeyEncrypted,
          personalWalletSecret
        ) => {
          renderParams.onProceed({
            walletType: 'keystore',
            walletAddress: publicKey.toLowerCase(),
            walletAddressOriginal: publicKey,
            privateKeyEncrypted,
            privateKeyPersonalEncryption: 'unlock',
            personalWalletSecret,
          });
        }}
        onBack={renderParams.onBackFromDetail}
        layout={renderParams.layout}
      />
    ),
  },
  softwareDiscovered: discoveredWallet('softwareDiscovered'),
  softwareDiscovered_metaMask: discoveredWallet('softwareDiscovered_metaMask'),
  softwareDiscovered_trustWallet: discoveredWallet(
    'softwareDiscovered_trustWallet'
  ),
  // softwareWeb3: softwareWeb3Wallet('softwareWeb3'),
  coinbaseWallet: {
    ...walletsMap['coinbaseWallet'],
    getWalletAsync: async () => {
      const coinbaseWallet = await unlockCoinbaseWallet();
      if (!coinbaseWallet) {
        throw new Error('Unknown problem');
      }
      const { publicKey } = coinbaseWallet;
      return {
        walletType: 'coinbaseWallet',
        walletAddress: publicKey.toLowerCase(),
        walletAddressOriginal: publicKey,
      };
    },
  },
  walletConnect: {
    ...walletsMap['walletConnect'],
    getWalletAsync: async () => {
      const walletConnect = await unlockWalletConnect().then(
        getWalletConnectWallet
      );
      if (!walletConnect) {
        throw new Error('Cannot find available wallet');
      }
      const { publicKey } = walletConnect;
      return {
        walletType: 'walletConnect',
        walletAddress: publicKey.toLowerCase(),
        walletAddressOriginal: publicKey,
      };
    },
  },
  // ledger: {
  //   ...walletsMap['ledger'],
  //   render: renderParams => (
  //     <UnlockHardwareWallet
  //       type="ledger"
  //       onUnlock={async (publicKey, path) => {
  //         renderParams.onProceed({
  //           walletType: 'ledger',
  //           walletAddress: publicKey.toLowerCase(),
  //           walletAddressOriginal: publicKey,
  //           hardwareWalletPath: path,
  //         });
  //       }}
  //     />
  //   ),
  // },
  // trezor: {
  //   ...walletsMap['trezor'],
  //   render: renderParams => (
  //     <UnlockHardwareWallet
  //       type="trezor"
  //       onUnlock={async (publicKey, path) => {
  //         renderParams.onProceed({
  //           walletType: 'trezor',
  //           walletAddress: publicKey.toLowerCase(),
  //           walletAddressOriginal: publicKey,
  //           hardwareWalletPath: path,
  //         });
  //       }}
  //     />
  //   ),
  // },
};
